<!--
  Settings → Dependency Sync (administrators only).

  "Dependencies" = the robots the ERP depends on for data it does not produce
  itself. One tile per robot, grouped by the system it talks to, with its
  state in one word and one sentence, and a banner that sums them up. Click a
  tile to open its detail (status, runs, settings and mail, how it works).
    SUMAL → ERP  SumalX1n (avize: productions and receptions)
                 SumalX2n (depots, permits, stock, yard entries; key "sumalx2"
                 in the database, called SumalX2 until 2026-10-18)
    ERP → SAGA   SagaX1n (sales invoices) and SagaX2n (productions), live since
                 2026-10-21, replace the C# robots of the SAGA computer. Their
                 "Test" (one document, check or send) and "Documents" tabs are
                 parts/SagaPanel.vue; data from GET /api/sagasync/overview.
  What each robot is and does is written in services.js; the live state comes
  from GET /api/sumal1n/status.

  The other mails (automatic mails, mail check) had two tabs here until
  2026-10-18: they are in Settings → Notifications now, and ?tab=mails /
  ?tab=mail-check redirect there. The service and its view are in the URL
  (?service=, ?view=), so a link can open a robot's settings directly.

  Plain English on purpose: short sentences, everyday words. The people who
  read this page at 7 in the morning are not developers.
-->
<template>
  <div class="hv2 sumal-sync">
    <!-- ================= TOP BAR ================= -->
    <div class="ds-topbar">
      <p class="hv2-sec-sub ds-topbar-sub">The robots that copy data between the ERP and the systems around it: what they do, whether they work, and their settings.</p>
      <div class="ds-topbar-actions">
        <span v-if="checkedAt" class="hv2-muted">Read {{ roWhen(checkedAt, now) }} · refreshes every minute</span>
        <a :href="$router.resolve({ path: '/settings/notifications' }).href" class="ds-topbar-link"
           @click.exact.prevent="$router.push({ path: '/settings/notifications' })">
          <feather-icon icon="BellIcon" svgClasses="w-3 h-3" /> Other mails: Notifications
        </a>
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw"
                   :disabled="loading" @click="refreshAll">Refresh</vs-button>
      </div>
    </div>

    <!-- ================= SERVICES ================= -->
    <div>
      <div v-if="loading && !status" key="l" class="hv2-loading">
        <vs-progress indeterminate color="primary" class="mb-2" />Reading…
      </div>
      <div v-else-if="error && !status" key="e" class="hv2-error">
        <b>The status could not be read.</b><br />{{ error }}
      </div>

      <div v-else-if="status" key="s">
        <!-- The answer to "is everything fine?", in one line -->
        <div class="ds-banner" :class="`is-${overall.tone}`">
          <feather-icon :icon="overall.icon" svgClasses="w-10 h-10" class="ds-banner-icon" />
          <div class="ds-banner-text">
            <div class="ds-banner-title">{{ overall.title }}</div>
            <div class="ds-banner-line">{{ overall.line }}</div>
            <ul v-if="overall.problems.length" class="ds-banner-list">
              <li v-for="p in overall.problems" :key="p.key">
                <a href="#" @click.prevent="selectService(p.key, true)"><b>{{ p.name }}</b></a>
                <span class="ds-word" :class="`is-${p.tone}`">{{ p.label }}</span> {{ p.line }}
              </li>
            </ul>
          </div>
        </div>
        <div v-if="error" class="hv2-error mb-base">The last refresh failed ({{ error }}). What you see was read {{ roWhen(checkedAt, now) }}.</div>

        <!-- The board: one tile per service, grouped by system -->
        <div class="ds-board">
          <section v-for="f in families" :key="f.key" class="ds-family">
            <div class="ds-family-head">
              <span class="ds-family-name">{{ f.name }}</span>
              <span class="ds-family-what">{{ f.what }}</span>
            </div>
            <div class="ds-family-tiles">
              <service-tile v-for="s in f.services" :key="s.key" :service="s" :src="srcOf(s)"
                            :health="healthMap[s.key]" :metrics="metricsOf(s)" :selected="selected === s.key"
                            @select="selectService($event, true)" />
            </div>
          </section>
        </div>

        <!-- The detail of the selected service -->
        <vx-card v-if="current" ref="detail" class="ds-detail">
          <div class="ds-detail-head">
            <div class="ds-detail-id">
              <div class="ds-detail-name">
                {{ current.name }}
                <span class="ds-flow">
                  <span>{{ current.from }}</span><feather-icon icon="ArrowRightIcon" svgClasses="w-3 h-3" /><span>{{ current.to }}</span>
                </span>
                <span class="ds-detail-topic">{{ current.topic }}</span>
              </div>
              <p class="hv2-sec-sub">{{ current.what }}</p>
            </div>
            <hv-pill class="ds-detail-pill" :tone="currentHealth.tone" :label="currentHealth.label" :icon="currentHealth.icon" />
          </div>

          <!-- A planned service: what it will be -->
          <template v-if="!current.live">
            <div class="hv2-verdict"><b>Coming soon.</b> {{ current.today }}</div>
            <div class="vx-row mt-base">
              <div class="vx-col w-full lg:w-1/2">
                <div class="hv2-kv"><div class="hv2-kv-k">Which way</div><div class="hv2-kv-v">From the {{ current.from }} to {{ current.to }}</div></div>
                <div class="hv2-kv"><div class="hv2-kv-k">Will replace</div><div class="hv2-kv-v">{{ current.replaces }}</div></div>
              </div>
              <div class="vx-col w-full lg:w-1/2">
                <div class="hv2-kv"><div class="hv2-kv-k">When it is live</div>
                  <div class="hv2-kv-v ds-kv-text">It gets the same card as the SUMAL robots: its state on the board, its planned hours, every run, its mail and its settings. Nothing to set up here before.</div></div>
              </div>
            </div>
          </template>

          <div v-else-if="!currentSrc" class="hv2-error">The ERP sent no status for {{ current.name }}.</div>

          <!-- A live service -->
          <template v-else>
            <div class="hv2-nav ds-subnav" role="tablist">
              <div v-for="t in detailTabs" :key="t.key" class="hv2-nav-item" :class="{ 'is-active': detailTab === t.key }"
                   role="tab" tabindex="0" :aria-selected="detailTab === t.key ? 'true' : 'false'"
                   @click="setDetailTab(t.key)" @keyup.enter="setDetailTab(t.key)">
                <feather-icon :icon="t.icon" svgClasses="w-4 h-4" />{{ t.label }}
                <span v-if="t.count" class="hv2-nav-count">{{ t.count }}</span>
              </div>
            </div>

            <!-- ---------- STATUS ---------- -->
            <div v-if="detailTab === 'status'" key="d-status">
              <div class="hv2-verdict" :class="currentHealth.tone ? `is-${currentHealth.tone}` : ''">
                <b>{{ currentHealth.label }}.</b> {{ currentHealth.line }}
                <template v-if="currentHealth.advice"><br />{{ currentHealth.advice }}</template>
              </div>

              <div class="vx-row mt-base">
                <div class="vx-col w-full lg:w-1/2">
                  <div class="hv2-subhead">The robot</div>
                  <div class="hv2-kv"><div class="hv2-kv-k">Runs at</div>
                    <div class="hv2-kv-v">{{ currentSrc.schedule }}
                      <div v-if="currentSrc.next_slot && currentSrc.state !== 'paused'" class="hv2-muted">next run {{ roWhen(currentSrc.next_slot, now) }}</div>
                    </div></div>
                  <div class="hv2-kv"><div class="hv2-kv-k">Last run</div>
                    <div class="hv2-kv-v" :class="{ 'is-empty': !currentSrc.last_run }">
                      <template v-if="currentSrc.last_run">
                        {{ roWhen(currentSrc.last_run.started_at, now) }}
                        <hv-pill :tone="runTone(currentSrc.last_run.status)" :label="runLabel(currentSrc.last_run.status)" class="ml-1" />
                        <span class="hv2-muted"> took {{ runDuration(currentSrc.last_run) }}</span>
                        <div class="hv2-muted">{{ runSummary(currentSrc.last_run) }}</div>
                        <div v-if="currentSrc.last_run.error" class="hv2-sync-error">{{ currentSrc.last_run.stage ? currentSrc.last_run.stage + ' · ' : '' }}{{ currentSrc.last_run.error }}</div>
                        <div v-else-if="currentSrc.last_run.warnings" class="hv2-sync-bad">{{ currentSrc.last_run.warnings }}</div>
                      </template>
                      <template v-else>never</template>
                    </div></div>
                  <div class="hv2-kv"><div class="hv2-kv-k">Last good run</div>
                    <div class="hv2-kv-v" :class="{ 'is-empty': !currentSrc.last_success }">
                      <template v-if="currentSrc.last_success">
                        {{ roWhen(currentSrc.last_success.started_at, now) }}
                        <span class="hv2-muted">· {{ ago(currentSrc.hours_since_success) }} ago · alarm after {{ currentSrc.late_hours }} h</span>
                      </template>
                      <template v-else>none</template>
                    </div></div>
                  <div class="hv2-kv"><div class="hv2-kv-k">Last failed run</div>
                    <div class="hv2-kv-v" :class="{ 'is-empty': !currentSrc.last_failure }">
                      <template v-if="currentSrc.last_failure">
                        {{ roWhen(currentSrc.last_failure.started_at, now) }}
                        <span v-if="currentSrc.last_failure.stage" class="hv2-muted">· step "{{ currentSrc.last_failure.stage }}"</span>
                        <div class="hv2-sync-error">{{ currentSrc.last_failure.error }}</div>
                      </template>
                      <template v-else>none</template>
                    </div></div>
                  <div class="hv2-kv"><div class="hv2-kv-k">Last 7 days</div>
                    <div class="hv2-kv-v">
                      {{ currentSrc.week.runs }} run(s): {{ currentSrc.week.ok }} good<template v-if="currentSrc.week.partial">, {{ currentSrc.week.partial }} incomplete</template>,
                      <span :class="{ 'hv2-sync-bad': currentSrc.week.failed }">{{ currentSrc.week.failed }} failed</span>
                    </div></div>
                  <div class="hv2-kv"><div class="hv2-kv-k">Where</div>
                    <div class="hv2-kv-v ds-kv-text">{{ current.host }} · <span class="hv2-code">{{ current.dir }}</span>
                      <div class="hv2-muted">log <span class="hv2-code">{{ current.log }}</span></div>
                    </div></div>
                </div>

                <div class="vx-col w-full lg:w-1/2">
                  <div class="hv2-subhead">What it brought</div>
                  <!-- SagaX1n / SagaX2n: what is in SAGA, what waits -->
                  <template v-if="current.family === 'saga'">
                    <template v-if="sagaOf(current.key)">
                      <div class="hv2-kv"><div class="hv2-kv-k">Sent in 7 days</div>
                        <div class="hv2-kv-v">{{ sagaOf(current.key).counts.sent_7d }} {{ current.key === 'sagax1n' ? 'invoice(s)' : 'production(s)' }}</div></div>
                      <div class="hv2-kv"><div class="hv2-kv-k">Waiting</div>
                        <div class="hv2-kv-v">{{ sagaOf(current.key).waiting.filter(r => !r.held).length }} for the next run<span class="hv2-muted"> · {{ sagaOf(current.key).today }} of today</span>
                          <div v-if="sagaOf(current.key).waiting.some(r => r.held)" class="hv2-muted">{{ sagaOf(current.key).waiting.filter(r => r.held).length }} waiting for "Ready to be sent to SAGA" on the invoice</div>
                          <div v-if="sagaOf(current.key).counts.older" class="hv2-muted">{{ sagaOf(current.key).counts.older }} older than {{ sagaOf(current.key).lookback_days }} days, not in SAGA (sent only one by one)</div>
                          <a href="#" class="hv2-muted" @click.prevent="setDetailTab('documents')">see them</a>
                        </div></div>
                      <div class="hv2-kv"><div class="hv2-kv-k">To look at</div>
                        <div class="hv2-kv-v">
                          <a v-if="sagaOf(current.key).to_look_at.length" href="#" class="hv2-sync-bad" @click.prevent="setDetailTab('documents')">{{ sagaOf(current.key).to_look_at.length }} not sent — open</a>
                          <span v-else class="hv2-muted">nothing</span>
                        </div></div>
                      <div class="hv2-kv"><div class="hv2-kv-k">Validation</div>
                        <div class="hv2-kv-v ds-kv-text">Never by the robot: the accountant validates in SAGA.</div></div>
                    </template>
                    <div v-else-if="sagaError" class="hv2-error">{{ sagaError }}</div>
                    <div v-else class="hv2-muted">Reading…</div>
                  </template>
                  <!-- SumalX1n: what the avize import produced -->
                  <template v-else-if="current.key === 'sumalx1n'">
                    <div class="hv2-kv"><div class="hv2-kv-k">Last harvest transport</div>
                      <div class="hv2-kv-v" :class="{ 'is-empty': !avize.last_harvest }">
                        <template v-if="avize.last_harvest">
                          {{ date(avize.last_harvest.op_date) }} · {{ avize.last_harvest.apv_label || avize.last_harvest.apv_code }} · {{ m3(avize.last_harvest.volume_m3) }} m³
                          <a v-if="avize.last_harvest.idoperation" :href="`/web/production/${avize.last_harvest.idoperation}`" target="_blank">#{{ avize.last_harvest.idoperation }}</a>
                          <div class="hv2-muted hv2-code">{{ avize.last_harvest.cod_aviz }}</div>
                        </template>
                        <template v-else>none yet</template>
                      </div></div>
                    <div class="hv2-kv"><div class="hv2-kv-k">Last reception</div>
                      <div class="hv2-kv-v" :class="{ 'is-empty': !avize.last_reception }">
                        <template v-if="avize.last_reception">
                          {{ date(avize.last_reception.op_date) }} · {{ avize.last_reception.company_name || DASH }} · {{ m3(avize.last_reception.volume_m3) }} m³
                          <a v-if="avize.last_reception.idoperation" :href="`/web/reception/${avize.last_reception.idoperation}`" target="_blank">#{{ avize.last_reception.idoperation }}</a>
                          <div class="hv2-muted hv2-code">{{ avize.last_reception.cod_aviz }}</div>
                        </template>
                        <template v-else>none yet</template>
                      </div></div>
                    <div class="hv2-kv"><div class="hv2-kv-k">Imported in 7 days</div>
                      <div class="hv2-kv-v">{{ avize.counts.imported_7d }} aviz · {{ m3(avize.counts.imported_7d_m3) }} m³</div></div>
                    <div class="hv2-kv"><div class="hv2-kv-k">To look at</div>
                      <div class="hv2-kv-v">
                        <a v-if="lookCount" href="#" class="hv2-sync-bad" @click.prevent="setDetailTab('look')">{{ lookCount }} line(s) — open</a>
                        <span v-else class="hv2-muted">nothing</span>
                      </div></div>
                    <div class="hv2-kv"><div class="hv2-kv-k">Operations created per day</div>
                      <div class="hv2-kv-v">
                        <span v-if="!avize.operations_per_day.length" class="hv2-muted">none in 8 days</span>
                        <span v-for="d in avize.operations_per_day" :key="d.day" class="hv2-sync-day">
                          {{ date(d.day).slice(0, 5) }}: <b>{{ d.harvest }}</b> H · <b>{{ d.reception }}</b> R
                        </span>
                        <div v-if="avize.operations_per_day.length" class="hv2-muted">H = harvest transport (production) · R = reception</div>
                      </div></div>
                  </template>

                  <!-- SumalX2n: the register -->
                  <template v-else-if="current.key === 'sumalx2'">
                    <div class="hv2-kv"><div class="hv2-kv-k">Depots in SUMAL</div>
                      <div class="hv2-kv-v">{{ status.depots.total }} · {{ status.depots.active }} active
                        <div v-if="status.depots.last_seen" class="hv2-muted">read {{ roWhen(status.depots.last_seen, now) }}</div></div></div>
                    <div class="hv2-kv"><div class="hv2-kv-k">APV codes without an ERP product</div>
                      <div class="hv2-kv-v">
                        <span :class="{ 'hv2-sync-bad': status.depots.pending }">{{ status.depots.pending }}</span>
                        <div v-if="status.depots.pending_codes.length" class="hv2-muted hv2-code">{{ status.depots.pending_codes.join(' · ') }}</div>
                        <div v-if="status.depots.pending" class="hv2-muted">New APV to encode in Harvesting 2.0. The morning report lists them too.</div>
                      </div></div>
                    <div class="hv2-kv"><div class="hv2-kv-k">Yard entries (TEIU)</div>
                      <div class="hv2-kv-v" :class="{ 'is-empty': !status.intrari || !status.intrari.last_read }">
                        <template v-if="status.intrari && status.intrari.last_read">
                          {{ status.intrari.rows }} known
                          <div class="hv2-muted">read {{ roWhen(status.intrari.last_read, now) }}</div>
                        </template>
                        <template v-else>never read</template>
                      </div></div>
                  </template>
                </div>
              </div>

              <div class="hv2-subhead mt-base">Planned hours, last 3 days</div>
              <slot-strip :slots="currentSrc.slots" mode="full" />
            </div>

            <!-- ---------- TO LOOK AT (SumalX1n) ---------- -->
            <div v-else-if="detailTab === 'look'" key="d-look">
              <hv-empty v-if="!lookCount" icon="CheckCircleIcon" title="Nothing to look at"
                        text="Every aviz SUMAL shows was imported, or is waiting for its next run." />

              <template v-if="avize.alerts.length">
                <div class="hv2-subhead">Imported, then rejected or cancelled in SUMAL</div>
                <p class="hv2-sec-sub">The operation exists in the ERP, but SUMAL no longer accepts the aviz. Check if the wood really moved. If not, deactivate the operation. The robot also says if the same truck got a new aviz that day.</p>
                <hv-table :rows="avize.alerts" row-key="cod_aviz" pagination :max-items="10">
                  <template slot="thead">
                    <vs-th>Date</vs-th><vs-th>Type</vs-th><vs-th>Aviz</vs-th><vs-th>Parcel / supplier</vs-th><vs-th class="hv2-col-n">m³</vs-th><vs-th>SUMAL now says</vs-th><vs-th>What we know</vs-th><vs-th>Operation</vs-th>
                  </template>
                  <template slot="row" slot-scope="{ r }">
                    <vs-td>{{ date(r.op_date) }}</vs-td>
                    <vs-td>{{ flowLabel(r.flow) }}</vs-td>
                    <vs-td class="hv2-code">{{ r.cod_aviz }}</vs-td>
                    <vs-td>{{ r.flow === 'harvest' ? (r.apv_label || r.apv_code) : (r.company_name || DASH) }}</vs-td>
                    <vs-td class="hv2-col-n">{{ m3(r.volume_m3) }}</vs-td>
                    <vs-td><hv-pill tone="danger" :label="sumalStatus(r.status)" icon="AlertTriangleIcon" /></vs-td>
                    <vs-td>{{ r.status_alert || DASH }}</vs-td>
                    <vs-td><a v-if="r.idoperation" :href="opLink(r)" target="_blank">#{{ r.idoperation }}</a></vs-td>
                  </template>
                </hv-table>
              </template>

              <template v-if="avize.problems.length">
                <div class="hv2-subhead mt-base">Not imported</div>
                <p class="hv2-sec-sub">The robot tried and could not. The reason is in the last column. It tries again at every run.</p>
                <hv-table :rows="avize.problems" row-key="cod_aviz" pagination :max-items="10">
                  <template slot="thead">
                    <vs-th>Date</vs-th><vs-th>Type</vs-th><vs-th>Aviz</vs-th><vs-th>Parcel / supplier</vs-th><vs-th class="hv2-col-n">m³</vs-th><vs-th>State</vs-th><vs-th>Reason</vs-th>
                  </template>
                  <template slot="row" slot-scope="{ r }">
                    <vs-td>{{ date(r.op_date) }}</vs-td>
                    <vs-td>{{ flowLabel(r.flow) }}</vs-td>
                    <vs-td class="hv2-code">{{ r.cod_aviz }}</vs-td>
                    <vs-td>{{ r.flow === 'harvest' ? (r.apv_label || r.apv_code) : (r.company_name || DASH) }}</vs-td>
                    <vs-td class="hv2-col-n">{{ m3(r.volume_m3) }}</vs-td>
                    <vs-td><hv-pill :tone="stateTone(r.import_state)" :label="stateLabel(r.import_state)" /></vs-td>
                    <vs-td>{{ r.import_note || DASH }}</vs-td>
                  </template>
                </hv-table>
              </template>

              <template v-if="avize.absent.length">
                <div class="hv2-subhead mt-base">At the yard in SUMAL, not in the ERP</div>
                <p class="hv2-sec-sub">SUMAL shows these trucks at the yard more than 6 hours ago. No active operation has their aviz number. The next run imports them, unless their status or details block it (see "Not imported").</p>
                <hv-table :rows="avize.absent" row-key="cod_aviz" pagination :max-items="10">
                  <template slot="thead">
                    <vs-th>Received</vs-th><vs-th>Aviz</vs-th><vs-th>From</vs-th><vs-th>PV</vs-th><vs-th class="hv2-col-n">m³</vs-th><vs-th>Import</vs-th>
                  </template>
                  <template slot="row" slot-scope="{ r }">
                    <vs-td>{{ date(r.pv_at) }} {{ String(r.pv_at || '').slice(11, 16) }}</vs-td>
                    <vs-td class="hv2-code">{{ r.cod_aviz }}</vs-td>
                    <vs-td>{{ r.company_name || DASH }}</vs-td>
                    <vs-td>{{ r.numar_pv || DASH }}</vs-td>
                    <vs-td class="hv2-col-n">{{ m3(r.m3) }}</vs-td>
                    <vs-td>
                      <hv-pill v-if="r.import_state" :tone="stateTone(r.import_state)" :label="stateLabel(r.import_state)" :title="r.import_note || ''" />
                      <span v-else class="hv2-muted">not seen yet</span>
                    </vs-td>
                  </template>
                </hv-table>
              </template>

              <template v-if="avize.junk.length">
                <div class="hv2-subhead mt-base">Operations with a wrong aviz number</div>
                <p class="hv2-sec-sub">Created by the old robot when it misread a file. Check them, then deactivate.</p>
                <div class="hv2-kv-v">
                  <span v-for="j in avize.junk" :key="j.id" class="hv2-muted mr-3">
                    <a :href="`/web/${j.category === 3 ? 'production' : 'reception'}/${j.id}`" target="_blank">#{{ j.id }}</a> "{{ j.sumal_aviz_nr }}"
                  </span>
                </div>
              </template>
            </div>

            <!-- ---------- AVIZE (SumalX1n) ---------- -->
            <div v-else-if="detailTab === 'avize'" key="d-avize">
              <div class="ds-toolbar">
                <p class="hv2-sec-sub">Every aviz read in SUMAL, with its SUMAL status and what the ERP did with it. Search by aviz number, parcel, supplier or plate.</p>
                <div class="ds-toolbar-filters">
                  <vs-input v-model="avizeQuery" placeholder="Search…" class="sumal-filter mr-2" @keyup.enter="loadAvize" />
                  <vs-select v-model="avizeFlow" class="sumal-filter mr-2" @input="loadAvize">
                    <vs-select-item value="" text="Both types" />
                    <vs-select-item value="harvest" text="Harvest" />
                    <vs-select-item value="reception" text="Reception" />
                  </vs-select>
                  <vs-select v-model="avizeState" class="sumal-filter" @input="loadAvize">
                    <vs-select-item value="" text="All" />
                    <vs-select-item value="imported" text="Imported" />
                    <vs-select-item value="existing" text="Already in ERP" />
                    <vs-select-item value="skipped" text="Skipped" />
                    <vs-select-item value="pending" text="No details" />
                    <vs-select-item value="error" text="Failed" />
                  </vs-select>
                </div>
              </div>
              <hv-table :rows="avizeRows" :loading="avizeLoading" :error="avizeError" row-key="cod_aviz" pagination :max-items="20"
                        empty-icon="FileTextIcon" empty-text="No aviz found.">
                <template slot="thead">
                  <vs-th>Date</vs-th><vs-th>Type</vs-th><vs-th>Aviz</vs-th><vs-th>Parcel / supplier</vs-th>
                  <vs-th class="hv2-col-n">m³</vs-th><vs-th>Truck</vs-th><vs-th>SUMAL status</vs-th><vs-th>In the ERP</vs-th><vs-th>Operation</vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td>{{ date(r.op_date) }}</vs-td>
                  <vs-td>{{ flowLabel(r.flow) }}</vs-td>
                  <vs-td class="hv2-code">{{ r.cod_aviz }}</vs-td>
                  <vs-td>
                    <template v-if="r.flow === 'harvest'">{{ r.apv_label || DASH }} <span class="hv2-muted hv2-code">{{ r.apv_code }}</span></template>
                    <template v-else>{{ r.company_name || DASH }}<span v-if="r.company_cui" class="hv2-muted"> · CUI {{ r.company_cui }}</span></template>
                  </vs-td>
                  <vs-td class="hv2-col-n">{{ m3(r.volume_m3) }}</vs-td>
                  <vs-td>{{ r.plate || DASH }}</vs-td>
                  <vs-td><span :class="{ 'hv2-sync-bad': r.status_alert }" :title="r.status || ''">{{ sumalStatus(r.status) }}</span></vs-td>
                  <vs-td><hv-pill :tone="stateTone(r.import_state)" :label="stateLabel(r.import_state)" :title="r.import_note || ''" /></vs-td>
                  <vs-td><a v-if="r.idoperation" :href="opLink(r)" target="_blank">#{{ r.idoperation }}</a><span v-else class="hv2-muted">{{ DASH }}</span></vs-td>
                </template>
              </hv-table>
            </div>

            <!-- ---------- TEST / DOCUMENTS (SagaX1n, SagaX2n) ---------- -->
            <div v-else-if="detailTab === 'test' || detailTab === 'documents'" key="d-saga">
              <saga-panel :service="current" :tab="detailTab" :overview="sagaOf(current.key)" :now="now"
                          :open-id="sagaOpenId" @refresh="loadSaga" @test="openSagaTest" />
            </div>

            <!-- ---------- RUNS ---------- -->
            <div v-else-if="detailTab === 'runs'" key="d-runs">
              <p class="hv2-sec-sub mb-3">One line per run of {{ current.name }}, newest first. A planned hour with no line means the robot did not start (see Status).</p>
              <hv-table :rows="currentRuns" :loading="runsLoading && !runs.length" :error="runsError" row-key="id" pagination :max-items="15"
                        empty-icon="ClockIcon" empty-text="No run yet.">
                <template slot="thead">
                  <vs-th>Started</vs-th><vs-th class="hv2-col-n">Took</vs-th><vs-th>Result</vs-th>
                  <vs-th>What it did</vs-th><vs-th>Mail</vs-th><vs-th>Error / warning</vs-th><vs-th></vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td class="hv2-nowrap">{{ roWhen(r.started_at, now) }}</vs-td>
                  <vs-td class="hv2-col-n">{{ runDuration(r) }}</vs-td>
                  <vs-td><hv-pill :tone="runTone(r.status)" :label="runLabel(r.status)" /></vs-td>
                  <vs-td>{{ runSummary(r) }}</vs-td>
                  <vs-td>
                    <span v-if="r.mail_sent_at" :title="r.mail_to || ''">sent {{ roTime(r.mail_sent_at) }}</span>
                    <span v-else-if="r.mail_error" class="hv2-sync-bad" :title="r.mail_error">failed</span>
                    <span v-else class="hv2-muted">{{ DASH }}</span>
                  </vs-td>
                  <vs-td>
                    <span v-if="r.stage && r.error" class="hv2-muted">{{ r.stage }} · </span>
                    <span :class="{ 'hv2-sync-bad': r.error }">{{ r.error || r.warnings || '' }}</span>
                  </vs-td>
                  <vs-td>
                    <div class="ds-actions">
                      <vs-button size="small" type="flat" icon-pack="feather" icon="icon-eye" title="See the mail" @click="previewMail(r)" />
                      <vs-button size="small" type="flat" icon-pack="feather" icon="icon-send" title="Send the mail again"
                                 :disabled="mailing === r.id" @click="confirmResend(r)" />
                    </div>
                  </vs-td>
                </template>
              </hv-table>
            </div>

            <!-- ---------- SETTINGS ---------- -->
            <div v-else-if="detailTab === 'settings'" key="d-settings" class="sumal-settings">
              <div class="sumal-settings-row">
                <vs-switch v-model="forms[current.key].enabled" />
                <span class="ml-2">{{ forms[current.key].enabled ? 'On. The robot runs at the hours below.' : 'Paused. The robot checks this before each run and does nothing.' }}</span>
              </div>
              <div class="vx-row items-end">
                <div class="vx-col md:w-1/2 w-full mb-3">
                  <vs-input class="w-full" label="Hours, Romania time (06:20, 12:20, …)" placeholder="06:20, 12:20, 18:20, 22:20"
                            v-model="forms[current.key].schedule" />
                </div>
                <div class="vx-col md:w-1/2 w-full mb-3">
                  <vs-select v-model="forms[current.key].mail_mode" label="Mail" class="w-full">
                    <vs-select-item value="always" text="After every run" />
                    <vs-select-item value="changes" text="Only when something is new or wrong" />
                    <vs-select-item value="errors" text="Only on error (the evening health mail says the rest)" />
                    <vs-select-item value="never" text="Never" />
                  </vs-select>
                </div>
                <div v-if="current.key === 'sumalx1n' || current.family === 'saga'" class="vx-col md:w-1/2 w-full mb-3">
                  <vs-input type="number" min="3" max="400" class="w-full" :label="current.family === 'saga' ? 'Days back the run sends (older: one by one, Test tab)' : 'Days read back on each run'"
                            v-model.number="forms[current.key].lookback_days" />
                </div>
              </div>
              <p v-if="current.family === 'saga'" class="hv2-sec-sub mb-3">The VPS asks every minute: the run goes at these hours (Romania time), with the documents dated before today. Paused, the robot still does the tests asked in the Test tab. Born paused on 21 October 2026: switch it on once a test went well.</p>
              <p v-else class="hv2-sec-sub mb-3">The VPS asks every 5 minutes if one of these hours has passed. Keep SumalX2n before SumalX1n, and both before the 07:00 morning report.</p>
              <vs-textarea v-model="forms[current.key].recipients" label="Who receives the mail, one address per line"
                           rows="3" class="w-full" />
              <div class="sumal-settings-actions">
                <vs-button size="small" :disabled="saving[current.key]" @click="saveSettings(current.key)">
                  {{ saving[current.key] ? 'Saving…' : 'Save' }}
                </vs-button>
                <vs-button size="small" type="border" icon-pack="feather" icon="icon-eye" class="ml-2"
                           :disabled="!currentSrc.last_run" @click="previewMail(currentSrc.last_run)">See last mail</vs-button>
                <vs-button size="small" type="border" icon-pack="feather" icon="icon-send" class="ml-2"
                           :disabled="!currentSrc.last_run || mailing === currentSrc.last_run.id" @click="confirmResend(currentSrc.last_run)">
                  {{ mailing === (currentSrc.last_run && currentSrc.last_run.id) ? 'Sending…' : 'Send last mail again' }}
                </vs-button>
                <span v-if="currentSrc.settings.updated_at" class="hv2-muted ml-3">
                  saved {{ roWhen(currentSrc.settings.updated_at, now) }}<template v-if="currentSrc.settings.updated_by"> by {{ currentSrc.settings.updated_by }}</template>
                </span>
              </div>
            </div>

            <!-- ---------- HOW IT WORKS ---------- -->
            <div v-else-if="detailTab === 'how'" key="d-how" class="ds-how">
              <div v-for="p in current.how(currentSrc)" :key="p.k" class="ds-how-row">
                <div class="ds-how-k">{{ p.k }}</div>
                <!-- Static text of services.js; the few values inserted are escaped there. -->
                <div class="ds-how-v" v-html="p.html" />
              </div>
              <p v-if="current.family === 'sumal'" class="hv2-muted mt-3">Both SUMAL robots use the same SUMAL account. Each keeps its own session for a day.</p>
              <p v-else-if="current.family === 'saga'" class="hv2-muted mt-3">Both SAGA robots use the same SAGA Web account and the same authorised computer, one after the other. SAGA renews the authorisation at every login: never copy the robots' state file to another machine.</p>
            </div>
          </template>
        </vx-card>
      </div>
    </div>


    <mail-preview :open.sync="previewOpen" :loading="previewLoading" :subject="previewSubject" :html="previewHtml" />
  </div>
</template>

<script>
import axios from '@/axios.js'
import HvPill from '@/views/harvesting2/components/HvPill'
import HvTable from '@/views/harvesting2/components/HvTable'
import HvEmpty from '@/views/harvesting2/components/HvEmpty'
import formatMixin from '@/views/harvesting2/formatMixin'
import ServiceTile from './parts/ServiceTile'
import SlotStrip from './parts/SlotStrip'
import SagaPanel from './parts/SagaPanel'
import MailPreview from '@/views/configurations/notifications/parts/MailPreview'
import { FAMILIES, SERVICES, serviceByKey, healthOf, runLabel, runTone } from './services'
import { ago, roTime, roWhen } from '@/helpers/romaniaTime'

const STATE = {
  imported: ['ok', 'Imported'],
  existing: ['info', 'Already in ERP'],
  skipped: ['', 'Skipped'],
  pending: ['warn', 'No details'],
  error: ['danger', 'Failed']
}
// SUMAL's Romanian statuses, in plain words. The raw status stays in the tooltip.
const SUMAL_STATUS = {
  INREGISTRAT: 'registered',
  IN_TRANZIT: 'in transit',
  IN_CONTROL: 'under control',
  ACCEPTAT_CONFIRMAT_MANUAL: 'accepted',
  ACCEPTAT_CONFIRMAT_AUTOMAT: 'accepted',
  EDITAT: 'edited',
  EXPIRAT: 'expired',
  ANULAT: 'cancelled',
  RESPINS: 'rejected',
  CONFISCAT: 'seized'
}
const LIVE_KEYS = SERVICES.filter((s) => s.live).map((s) => s.key)
const REFRESH_MS = 60000

const emptyForm = () => ({
  enabled: true,
  mail_mode: 'always',
  recipients: '',
  lookback_days: 35,
  schedule: ''
})

export default {
  components: { HvPill, HvTable, HvEmpty, ServiceTile, SlotStrip, MailPreview, SagaPanel },
  mixins: [formatMixin],
  data () {
    const forms = {}
    const saving = {}
    LIVE_KEYS.forEach((k) => { forms[k] = emptyForm(); saving[k] = false })
    return {
      selected: null,
      detailTab: 'status',
      now: new Date(),
      timer: null,
      loading: true,
      error: '',
      status: null,
      checkedAt: null,
      forms,
      saving,
      mailing: null,
      runs: [],
      runsLoading: false,
      runsError: '',
      avizeRows: [],
      avizeLoaded: false,
      avizeLoading: false,
      avizeError: '',
      avizeQuery: '',
      avizeFlow: '',
      avizeState: '',
      saga: null,
      sagaError: '',
      sagaOpenId: null,
      previewOpen: false,
      previewLoading: false,
      previewHtml: '',
      previewSubject: ''
    }
  },
  computed: {
    families () {
      return FAMILIES.map((f) => ({ ...f, services: SERVICES.filter((s) => s.family === f.key) }))
    },
    healthMap () {
      const out = {}
      for (const s of SERVICES) out[s.key] = healthOf(s, this.srcOf(s), this.now)
      return out
    },
    // The banner: the worst state first, then the one sentence that matters.
    overall () {
      const live = SERVICES.filter((s) => s.live)
      const planned = SERVICES.filter((s) => !s.live)
      const soon = planned.length ? ` ${planned.map((s) => s.name).join(' and ')} ${planned.length > 1 ? 'are' : 'is'} coming soon.` : ''
      const all = live.map((s) => ({ key: s.key, name: s.name, ...this.healthMap[s.key] }))
      const problems = all.filter((h) => h.tone === 'danger' || h.tone === 'warn').sort((a, b) => b.rank - a.rank)
      const paused = all.filter((h) => h.label === 'Paused')
      const danger = problems.filter((h) => h.tone === 'danger')
      if (danger.length) {
        return {
          tone: 'danger',
          icon: 'XCircleIcon',
          title: danger.length === 1 ? `${danger[0].name} needs attention now` : `${danger.length} services need attention now`,
          line: `${danger.length === 1 ? 'Its' : 'Their'} data in the ERP is no longer up to date.`,
          problems
        }
      }
      if (problems.length) {
        return {
          tone: 'warn',
          icon: 'AlertTriangleIcon',
          title: problems.length === 1 ? `${problems[0].name} needs a look` : `${problems.length} services need a look`,
          line: 'The data still arrives, but not all of it, or not at every planned hour.',
          problems
        }
      }
      if (paused.length) {
        return {
          tone: 'info',
          icon: 'PauseCircleIcon',
          title: `${paused.map((p) => p.name).join(' and ')} ${paused.length > 1 ? 'are' : 'is'} paused`,
          line: `Switched off on purpose in the settings.${paused.length < live.length ? ' The other services work.' : ''}${soon}`,
          problems: []
        }
      }
      return {
        tone: 'ok',
        icon: 'CheckCircleIcon',
        title: `All ${live.length} services are working`,
        line: `${live.map((s) => s.name).join(' and ')} ran at their planned hours. The data in the ERP is up to date.${soon}`,
        problems: []
      }
    },
    current () { return serviceByKey(this.selected) },
    currentSrc () { return this.current ? this.srcOf(this.current) : null },
    currentHealth () { return this.current ? this.healthMap[this.current.key] : {} },
    currentRuns () { return this.current ? this.runs.filter((r) => r.source === this.current.key) : [] },
    avize () { return this.status.avize },
    lookCount () {
      const a = this.status && this.status.avize
      return a ? a.alerts.length + a.problems.length + a.absent.length + a.junk.length : 0
    },
    detailTabs () {
      if (!this.current || !this.current.live) return []
      const tabs = [{ key: 'status', label: 'Status', icon: 'ActivityIcon' }]
      if (this.current.key === 'sumalx1n') {
        tabs.push({ key: 'look', label: 'To look at', icon: 'AlertTriangleIcon', count: this.lookCount })
        tabs.push({ key: 'avize', label: 'Avize', icon: 'FileTextIcon' })
      }
      if (this.current.family === 'saga') {
        const o = this.sagaOf(this.current.key)
        tabs.push({ key: 'test', label: 'Test', icon: 'PlayCircleIcon' })
        tabs.push({ key: 'documents', label: 'Documents', icon: 'FileTextIcon', count: o ? o.to_look_at.length : 0 })
      }
      tabs.push({ key: 'runs', label: 'Runs', icon: 'ClockIcon' })
      tabs.push({ key: 'settings', label: 'Settings', icon: 'SettingsIcon' })
      tabs.push({ key: 'how', label: 'How it works', icon: 'BookOpenIcon' })
      return tabs
    }
  },
  methods: {
    roTime,
    roWhen,
    ago,
    runTone,
    runLabel,
    notify (title, text, color) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check', position: 'bottom-right', time: 6000 })
    },
    errText (e) { return (e.response && e.response.data && e.response.data.message) || e.message },
    srcOf (s) { return s.live && this.status && this.status.sources ? this.status.sources[s.key] || null : null },
    sagaOf (key) { return this.saga ? this.saga[key] || null : null },
    // "Test" on a line of Documents: open the Test tab on that operation.
    openSagaTest (id) {
      this.sagaOpenId = null
      this.$nextTick(() => { this.sagaOpenId = id; this.setDetailTab('test') })
    },
    // What the tile shows under the strip: the figure that says the robot is useful.
    metricsOf (s) {
      if (!this.status || !s.live) return []
      if (s.key === 'sumalx1n') {
        const c = this.status.avize.counts || {}
        const out = [{ text: `${c.imported_7d || 0} avize imported in 7 days · ${this.m3(c.imported_7d_m3 || 0)} m³` }]
        if (this.lookCount) out.push({ text: `${this.lookCount} to look at`, tone: 'warn' })
        return out
      }
      if (s.family === 'saga') {
        const o = this.sagaOf(s.key)
        if (!o) return []
        const what = s.key === 'sagax1n' ? 'invoice' : 'production'
        const held = o.waiting.filter(r => r.held).length
        const out = [{ text: `${o.counts.sent_7d || 0} ${what}${o.counts.sent_7d === 1 ? '' : 's'} sent in 7 days · ${o.waiting.length - held} waiting${held ? ` · ${held} to confirm` : ''}` }]
        if (o.to_look_at.length) out.push({ text: `${o.to_look_at.length} not sent, to look at`, tone: 'warn' })
        return out
      }
      if (s.key === 'sumalx2') {
        const d = this.status.depots || {}
        const out = [{ text: `${d.total || 0} depots in SUMAL · ${d.active || 0} active` }]
        if (d.pending) out.push({ text: `${d.pending} APV code${d.pending > 1 ? 's' : ''} without an ERP product`, tone: 'warn' })
        return out
      }
      return []
    },

    /* ------------------------------------------------ selection and URL */
    syncQuery () {
      const q = {}
      if (this.selected) q.service = this.selected
      if (this.detailTab !== 'status') q.view = this.detailTab
      this.$router.replace({ query: q }).catch(() => {})
    },
    selectService (key, byHand) {
      if (!serviceByKey(key)) return
      this.selected = key
      if (!this.detailTabs.some((t) => t.key === this.detailTab)) this.detailTab = 'status'
      this.syncQuery()
      if (byHand) {
        // Bring the detail into view only when it starts low on the screen.
        this.$nextTick(() => {
          const el = this.$refs.detail && this.$refs.detail.$el
          if (el && el.getBoundingClientRect().top > window.innerHeight * 0.6) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      }
    },
    setDetailTab (key) {
      this.detailTab = key
      if (key === 'avize' && !this.avizeLoaded) this.loadAvize()
      this.syncQuery()
    },
    // The default service: the one in the worst state, else the first.
    pickDefault () {
      if (this.selected) return
      const live = SERVICES.filter((s) => s.live)
      const worst = live.slice().sort((a, b) => this.healthMap[b.key].rank - this.healthMap[a.key].rank)[0]
      this.selected = worst && this.healthMap[worst.key].rank > 1 ? worst.key : live[0].key
    },

    /* ------------------------------------------------ loading */
    refreshAll () {
      this.load()
      this.loadRuns()
      this.loadSaga()
      if (this.avizeLoaded) this.loadAvize()
    },
    // silent = the minute refresh: no spinner, and the settings being typed are kept.
    load (silent = false) {
      if (!silent) this.loading = true
      axios.get('/sumal1n/status')
        .then((r) => {
          this.status = r.data
          this.checkedAt = new Date()
          this.now = new Date()
          this.error = ''
          if (!silent) {
            for (const key of LIVE_KEYS) {
              const s = (r.data.sources[key] && r.data.sources[key].settings) || {}
              this.forms[key] = {
                enabled: s.enabled !== false,
                mail_mode: s.mail_mode || 'always',
                recipients: String(s.recipients || '').split(';').filter(Boolean).join('\n'),
                lookback_days: Number(s.lookback_days) || 35,
                schedule: String(s.schedule || '').split(',').filter(Boolean).join(', ')
              }
            }
          }
          this.pickDefault()
          // A ?view= from an old link, or one this robot does not have.
          if (this.current && this.current.live && !this.detailTabs.some((t) => t.key === this.detailTab)) this.detailTab = 'status'
        })
        .catch((e) => { this.error = this.errText(e) })
        .finally(() => { this.loading = false })
    },
    // The SAGA robots: what waits, what failed, the last documents and tests.
    loadSaga () {
      axios.get('/sagasync/overview')
        .then((r) => { this.saga = r.data; this.sagaError = '' })
        .catch((e) => { this.sagaError = this.errText(e) })
    },
    loadRuns () {
      this.runsLoading = true
      this.runsError = ''
      axios.get('/sumal1n/runs?limit=240')
        .then((r) => { this.runs = r.data || [] })
        .catch((e) => { this.runsError = this.errText(e) })
        .finally(() => { this.runsLoading = false })
    },
    loadAvize () {
      this.avizeLoading = true
      this.avizeError = ''
      this.avizeLoaded = true
      const params = new URLSearchParams({ limit: '200' })
      if (this.avizeFlow) params.set('flow', this.avizeFlow)
      if (this.avizeState) params.set('state', this.avizeState)
      if (this.avizeQuery.trim()) params.set('q', this.avizeQuery.trim())
      axios.get(`/sumal1n/avize?${params}`)
        .then((r) => { this.avizeRows = r.data || [] })
        .catch((e) => { this.avizeError = this.errText(e) })
        .finally(() => { this.avizeLoading = false })
    },
    tick () {
      this.now = new Date()
      if (document.visibilityState !== 'visible' || this.loading) return
      this.load(true)
      this.loadRuns()
      this.loadSaga()
    },

    /* ------------------------------------------------ words */
    stateTone (st) { return (STATE[st] || ['', st])[0] },
    stateLabel (st) { return (STATE[st] || ['', st || '—'])[1] },
    sumalStatus (st) {
      if (!st) return this.DASH
      const key = String(st).toUpperCase().replace(/\s+/g, '_')
      return SUMAL_STATUS[key] || String(st).toLowerCase()
    },
    flowLabel (f) { return f === 'harvest' ? 'Harvest' : 'Reception' },
    opLink (r) { return `/web/${r.flow === 'harvest' ? 'production' : 'reception'}/${r.idoperation}` },
    runDuration (r) {
      if (!r.finished_at) return this.DASH
      const s = (new Date(r.finished_at) - new Date(r.started_at)) / 1000
      return s < 60 ? `${s.toFixed(1)} s` : `${Math.round(s / 60)} min`
    },
    runSummary (r) {
      if (r.source === 'sagax1n' || r.source === 'sagax2n') {
        if (r.ok === false) return r.mode === 'request' ? 'test' : ''
        const parts = [`${r.items_sent || 0} sent`]
        if (r.items_existing) parts.push(`${r.items_existing} already in SAGA`)
        if (r.items_checked) parts.push(`${r.items_checked} checked`)
        if (r.items_blocked) parts.push(`${r.items_blocked} not sent`)
        if (r.items_errors) parts.push(`${r.items_errors} refused by SAGA`)
        return `${r.mode === 'request' ? 'Test: ' : ''}${parts.join(' · ')}${r.details && r.details.dry_run ? ' (dry run)' : ''}`
      }
      if (r.ok === false) return r.avize_received === 0 && r.source === 'sumalx1n' ? 'nothing sent' : ''
      if (r.source === 'sumalx2') {
        const parts = [`${r.depots_received || 0} depots`]
        if (r.depots_new) parts.push(`${r.depots_new} new`)
        if (r.parcels_created) parts.push(`${r.parcels_created} parcels created`)
        if (r.intrari_received !== null && r.intrari_received !== undefined) parts.push(`${r.intrari_received} yard entries`)
        if (r.pending) parts.push(`${r.pending} APV without product`)
        return parts.join(' · ')
      }
      const parts = [`${r.avize_imported || 0} imported`]
      if (r.avize_existing) parts.push(`${r.avize_existing} already in ERP`)
      if (r.avize_skipped) parts.push(`${r.avize_skipped} skipped`)
      if (r.avize_errors) parts.push(`${r.avize_errors} not imported`)
      const d = r.details || {}
      if ((d.alerts || []).length) parts.push(`${d.alerts.length} to look at`)
      if ((d.created_products || []).length) parts.push(`${d.created_products.length} APV product(s) created`)
      if ((d.created_persons || []).length) parts.push(`${d.created_persons.length} supplier(s) created`)
      return `${parts.join(' · ')} (${r.avize_received || 0} seen)`
    },

    /* ------------------------------------------------ settings and mail */
    saveSettings (key) {
      this.saving[key] = true
      const f = this.forms[key]
      axios.put(`/sumal1n/settings/${key}`, {
        enabled: f.enabled,
        mail_mode: f.mail_mode,
        recipients: f.recipients,
        lookback_days: f.lookback_days,
        schedule: f.schedule
      })
        .then(() => {
          this.notify('Saved', (serviceByKey(key) || {}).name || key, 'success')
          this.load()
        })
        .catch((e) => this.notify('Not saved', this.errText(e), 'danger'))
        .finally(() => { this.saving[key] = false })
    },
    previewMail (run) {
      if (!run) return
      this.previewOpen = true
      this.previewLoading = true
      this.previewHtml = ''
      this.previewSubject = ''
      axios.get(`/sumal1n/runs/${run.id}/mail`)
        .then((r) => { this.previewHtml = r.data.html; this.previewSubject = r.data.subject })
        .catch((e) => { this.notify('Cannot show the mail', this.errText(e), 'danger'); this.previewOpen = false })
        .finally(() => { this.previewLoading = false })
    },
    // A send reaches real mailboxes: name the recipients before.
    confirmResend (run) {
      if (!run) return
      const form = this.forms[run.source] || this.forms.sumalx1n
      const to = form.recipients.split('\n').filter(Boolean).join(', ')
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Send the mail of run #${run.id} again?`,
        text: `To: ${to || '(no recipient)'}`,
        acceptText: 'Send',
        cancelText: 'Cancel',
        accept: () => this.resend(run)
      })
    },
    resend (run) {
      this.mailing = run.id
      axios.post(`/sumal1n/runs/${run.id}/mail`)
        .then((r) => {
          this.notify('Mail sent', `${r.data.subject} → ${r.data.to.join(', ')}`, 'success')
          this.loadRuns()
        })
        .catch((e) => this.notify('Mail not sent', this.errText(e), 'danger'))
        .finally(() => { this.mailing = null })
    }
  },
  created () {
    const roles = String(localStorage.getItem('login-roles') || '').toLowerCase()
    if (!roles.includes('admin')) {
      this.$vs.notify({
        title: 'Denied',
        text: 'This page is for administrators',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
        position: 'bottom-right',
        time: 8000
      })
      this.$router.push({ path: '/dashboard/analytics' })
      return
    }
    const q = this.$route.query || {}
    // Old links to the mail tabs of this page (until 2026-10-18).
    if (q.tab === 'mails' || q.tab === 'mail-check') {
      this.$router.replace({ path: '/settings/notifications', query: { tab: q.tab } }).catch(() => {})
      return
    }
    if (serviceByKey(q.service)) this.selected = q.service
    if (q.view) this.detailTab = String(q.view)
    if (this.detailTab === 'avize') this.loadAvize()
    this.load()
    this.loadRuns()
    this.loadSaga()
    this.timer = setInterval(this.tick, REFRESH_MS)
  },
  beforeDestroy () {
    if (this.timer) clearInterval(this.timer)
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/modules/harvesting2.scss";

.sumal-sync {
  --ds-card: #fff;

  .sumal-settings { margin-top: .5rem; }
  .sumal-settings-row { display: flex; align-items: center; margin-bottom: .75rem; }
  .sumal-settings-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 0; margin-top: .5rem; }
  .sumal-filter { min-width: 170px; display: inline-block; }
  .hv2-kv-v .hv2-pill { vertical-align: middle; }
  code, .ds-how-v code { font-size: 12px; padding: 1px 4px; border-radius: 3px; background: rgba(128,128,128,.12); }

  /* ------------------------------------------------------------ top bar */
  .ds-topbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px 16px; padding-bottom: 12px; margin-bottom: 18px; border-bottom: 1px solid var(--hv-rule-strong); }
  .ds-topbar-sub { margin: 0; }
  .ds-topbar-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 14px; }
  .ds-topbar-link { display: inline-flex; align-items: center; gap: 4px; font-size: .78rem; color: var(--hv-muted); }
  .ds-topbar-link:hover { color: var(--hv-sage-ink); }
  .ds-subnav .hv2-nav-item .feather-icon { margin-right: 6px; }

  /* -------------------------------------------- banner: is all fine? */
  .ds-banner {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 22px;
    border-radius: 12px;
    background: var(--ds-card);
    border-left: 6px solid var(--hv-rule-strong);
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, .06);
  }
  .ds-banner.is-ok { background: linear-gradient(var(--hv-ok-tint), var(--hv-ok-tint)), var(--ds-card); border-left-color: var(--hv-ok); }
  .ds-banner.is-warn { background: linear-gradient(var(--hv-warn-tint), var(--hv-warn-tint)), var(--ds-card); border-left-color: var(--hv-warn); }
  .ds-banner.is-danger { background: linear-gradient(var(--hv-danger-tint), var(--hv-danger-tint)), var(--ds-card); border-left-color: var(--hv-danger); }
  .ds-banner.is-info { background: linear-gradient(var(--hv-info-tint), var(--hv-info-tint)), var(--ds-card); border-left-color: var(--hv-info); }
  .ds-banner-icon { flex: 0 0 auto; color: var(--hv-muted); margin-top: 2px; }
  .ds-banner.is-ok .ds-banner-icon { color: var(--hv-ok); }
  .ds-banner.is-warn .ds-banner-icon { color: var(--hv-warn); }
  .ds-banner.is-danger .ds-banner-icon { color: var(--hv-danger); }
  .ds-banner.is-info .ds-banner-icon { color: var(--hv-info); }
  .ds-banner-text { min-width: 0; }
  .ds-banner-title { font-size: 1.4rem; font-weight: 700; line-height: 1.25; color: var(--hv-ink); }
  .ds-banner-line { font-size: .92rem; color: var(--hv-body); margin-top: 4px; line-height: 1.5; }
  .ds-banner-list { margin: 10px 0 0; padding: 0; list-style: none; font-size: .88rem; color: var(--hv-body); }
  .ds-banner-list li { margin-bottom: 4px; line-height: 1.5; }
  .ds-banner-list a { margin-right: 6px; }

  /* A state in one word, coloured AND written. */
  .ds-word { font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; margin-right: 4px; color: var(--hv-muted); }
  .ds-word.is-ok { color: var(--hv-ok); }
  .ds-word.is-warn { color: var(--hv-warn); }
  .ds-word.is-danger { color: var(--hv-danger); }
  .ds-word.is-info { color: var(--hv-info); }

  /* ----------------------------------------------------------- board */
  .ds-board { display: grid; grid-template-columns: minmax(0, 1fr); gap: 22px; margin-bottom: 22px; }
  @media (min-width: 1200px) { .ds-board { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  .ds-family { min-width: 0; }
  .ds-family-head { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 10px; margin-bottom: 10px; }
  .ds-family-name { font-size: .8rem; font-weight: 800; letter-spacing: .14em; color: var(--hv-ink); }
  .ds-family-what { font-size: .78rem; color: var(--hv-muted); }
  .ds-family-tiles { display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
  @media (min-width: 640px) { .ds-family-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

  /* ------------------------------------------------------------ tile */
  .ds-tile {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    height: 100%;
    padding: 16px 16px 14px;
    border-radius: 12px;
    background: var(--ds-card);
    border: 1px solid var(--hv-rule);
    box-shadow: inset 5px 0 0 var(--hv-rule-strong), 0 4px 24px 0 rgba(34, 41, 47, .06);
    cursor: pointer;
    outline: none;
    transition: border-color .15s, box-shadow .15s, transform .15s;
  }
  .ds-tile:hover { transform: translateY(-2px); }
  .ds-tile:focus-visible { border-color: var(--hv-info); }
  .ds-tile.is-ok { box-shadow: inset 5px 0 0 var(--hv-ok), 0 4px 24px 0 rgba(34, 41, 47, .06); }
  .ds-tile.is-warn { box-shadow: inset 5px 0 0 var(--hv-warn), 0 4px 24px 0 rgba(34, 41, 47, .06); }
  .ds-tile.is-danger { box-shadow: inset 5px 0 0 var(--hv-danger), 0 4px 24px 0 rgba(34, 41, 47, .06); }
  .ds-tile.is-info { box-shadow: inset 5px 0 0 var(--hv-info), 0 4px 24px 0 rgba(34, 41, 47, .06); }
  .ds-tile.is-selected { border-color: var(--hv-sage); outline: 2px solid var(--hv-sage); outline-offset: -1px; }
  .ds-tile.is-planned { background: transparent; border-style: dashed; border-color: var(--hv-rule-strong); box-shadow: none; }
  .ds-tile.is-planned.is-selected { border-style: solid; }

  .ds-tile-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
  .ds-tile-id { min-width: 0; }
  .ds-tile-name { font-size: 1.15rem; font-weight: 700; color: var(--hv-ink); line-height: 1.2; }
  .ds-tile-topic { font-size: .78rem; color: var(--hv-muted); margin-top: 2px; }

  .ds-flow {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
    padding: 2px 8px;
    border-radius: 6px;
    background: var(--hv-surface-2);
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .05em;
    color: var(--hv-body);
    white-space: nowrap;
    vertical-align: middle;
  }

  .ds-tile-state { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border-radius: 8px; background: var(--hv-surface); }
  .ds-tile.is-ok .ds-tile-state { background: var(--hv-ok-tint); }
  .ds-tile.is-warn .ds-tile-state { background: var(--hv-warn-tint); }
  .ds-tile.is-danger .ds-tile-state { background: var(--hv-danger-tint); }
  .ds-tile.is-info .ds-tile-state { background: var(--hv-info-tint); }
  .ds-tile-state-icon { flex: 0 0 auto; color: var(--hv-muted); }
  .ds-tile.is-ok .ds-tile-state-icon, .ds-tile.is-ok .ds-tile-state-label { color: var(--hv-ok); }
  .ds-tile.is-warn .ds-tile-state-icon, .ds-tile.is-warn .ds-tile-state-label { color: var(--hv-warn); }
  .ds-tile.is-danger .ds-tile-state-icon, .ds-tile.is-danger .ds-tile-state-label { color: var(--hv-danger); }
  .ds-tile.is-info .ds-tile-state-icon, .ds-tile.is-info .ds-tile-state-label { color: var(--hv-info); }
  .ds-tile-state-text { min-width: 0; }
  .ds-tile-state-label { font-size: 1.05rem; font-weight: 700; line-height: 1.3; color: var(--hv-ink); }
  .ds-tile-state-line { font-size: .78rem; color: var(--hv-body); line-height: 1.45; margin-top: 1px; }
  .is-spinning svg { animation: ds-spin 1.6s linear infinite; }

  .ds-tile-facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .ds-fact { min-width: 0; }
  .ds-fact-k { display: block; font-size: .68rem; text-transform: uppercase; letter-spacing: .04em; color: var(--hv-muted); }
  .ds-fact-v { display: block; font-size: .86rem; font-weight: 600; color: var(--hv-ink); }

  .ds-tile-metric { font-size: .8rem; color: var(--hv-body); display: flex; align-items: center; }
  .ds-tile-metric.is-warn { color: var(--hv-warn); font-weight: 600; }
  .ds-tile-metric.is-danger { color: var(--hv-danger); font-weight: 600; }
  .ds-tile-what { font-size: .82rem; color: var(--hv-body); line-height: 1.5; margin: 0; }
  .ds-tile.is-planned .ds-tile-metric { color: var(--hv-muted); }

  /* ------------------------------------------- planned hours (strip) */
  .ds-strip { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 12px; }
  .ds-strip-days { display: inline-flex; gap: 8px; }
  .ds-strip-day { display: inline-flex; gap: 3px; }
  .ds-strip-sum { font-size: .76rem; color: var(--hv-muted); }
  .ds-strip-sum.is-bad { color: var(--hv-danger); font-weight: 600; }
  .ds-mark { display: inline-block; width: 12px; height: 18px; border-radius: 3px; background: var(--hv-surface-2); vertical-align: middle; flex: 0 0 auto; }
  .ds-mark.is-ok { background: var(--hv-ok); }
  .ds-mark.is-partial { background: var(--hv-warn); }
  .ds-mark.is-failed { background: var(--hv-danger); }
  .ds-mark.is-missed { background: transparent; box-shadow: inset 0 0 0 2px var(--hv-danger); }
  .ds-mark.is-running { background: transparent; box-shadow: inset 0 0 0 2px var(--hv-info); }
  .ds-mark.is-paused { background: transparent; box-shadow: inset 0 0 0 2px var(--hv-rule-strong); }

  .ds-strip-full { font-size: .86rem; }
  .ds-strip-row { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; padding: 8px 0; border-bottom: 1px solid var(--hv-rule); }
  .ds-strip-rowday { flex: 0 0 110px; font-weight: 600; color: var(--hv-ink); }
  .ds-strip-rowslots { display: flex; flex-wrap: wrap; gap: 6px 18px; min-width: 0; }
  .ds-strip-slot { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
  .ds-strip-time { font-variant-numeric: tabular-nums; color: var(--hv-body); }
  .ds-strip-word { font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--hv-muted); }
  .ds-strip-word.is-ok { color: var(--hv-ok); }
  .ds-strip-word.is-warn { color: var(--hv-warn); }
  .ds-strip-word.is-danger { color: var(--hv-danger); }
  .ds-strip-word.is-info { color: var(--hv-info); }
  .ds-strip-legend { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 10px; }
  .ds-strip-legend > span { display: inline-flex; align-items: center; gap: 6px; }
  .ds-strip-legend .ds-mark { width: 9px; height: 13px; }

  /* ---------------------------------------------------------- detail */
  .ds-detail-head { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 10px 16px; margin-bottom: 14px; }
  .ds-detail-id { min-width: 0; }
  .ds-detail-name { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 1.35rem; font-weight: 700; color: var(--hv-ink); line-height: 1.25; }
  .ds-detail-topic { font-size: .86rem; font-weight: 500; color: var(--hv-muted); }
  .ds-detail-pill { font-size: .86rem; padding: 4px 12px; }
  .ds-detail-pill svg { width: 15px; height: 15px; }
  .ds-subnav { margin-bottom: 18px; }
  .ds-kv-text { font-weight: 500; font-size: .9rem; line-height: 1.5; }
  .ds-toolbar { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 12px; }
  .ds-toolbar-filters { display: flex; flex-wrap: wrap; gap: 6px 0; }
  .ds-actions { display: flex; flex-wrap: nowrap; }

  .ds-how-row { display: flex; flex-wrap: wrap; gap: 4px 20px; padding: 12px 0; border-bottom: 1px solid var(--hv-rule); }
  .ds-how-k { flex: 0 0 180px; font-weight: 700; color: var(--hv-ink); font-size: .9rem; }
  .ds-how-v { flex: 1 1 320px; min-width: 0; color: var(--hv-body); font-size: .9rem; line-height: 1.6; max-width: 90ch; }

  @media (max-width: 767px) {
    .ds-banner { padding: 14px 16px; gap: 12px; }
    .ds-banner-title { font-size: 1.15rem; }
    .ds-strip-rowday { flex-basis: 100%; }
    .ds-how-k { flex-basis: 100%; }
  }
}

.theme-dark .sumal-sync { --ds-card: #10163a; }

@keyframes ds-spin { to { transform: rotate(360deg); } }
</style>
